// Shared atoms for the Projector GUI — ported from the design handoff
// (docs/design/design_update/design_handoff_projector_gui/gui-atoms.jsx).
// Visual language (Soundbath foundation, push 3): flat surfaces stepped by
// 1px hairlines, no shadows, one signal colour for the primary action and
// work in motion, Archivo for text and IBM Plex Mono for machine values.
// Every colour and radius comes from tokens.css — boxes on --r-card /
// --r-ctl (6px), pill buttons, chips and the toggle on --r-pill.

import React from 'react';

export function GEyebrow({ children, style = {} }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 13.5, color: 'var(--fg-3)', letterSpacing: 0.2, ...style }}>{children}</div>;
}

export function GH2({ children, onCream = false, size = 20, style = {}, title }: {
  children: React.ReactNode; onCream?: boolean; size?: number; style?: React.CSSProperties; title?: string;
}) {
  return (
    <h2 title={title} style={{
      margin: 0, fontFamily: 'var(--ff-sans)', fontSize: size, fontWeight: 500,
      letterSpacing: '-0.02em', color: onCream ? 'var(--on-cream)' : 'var(--fg)',
      whiteSpace: 'nowrap', ...style,
    }}>{children}</h2>
  );
}

export function GDisplay({ children, size = 56, onCream = false, suffix }: {
  children: React.ReactNode; size?: number; onCream?: boolean; suffix?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, whiteSpace: 'nowrap' }}>
      <div className="display-num" style={{ fontSize: size, color: onCream ? 'var(--on-cream)' : 'var(--fg)', whiteSpace: 'nowrap' }}>{children}</div>
      {suffix && <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 13, color: onCream ? 'var(--on-cream-3)' : 'var(--fg-3)' }}>{suffix}</div>}
    </div>
  );
}

/**
 * The button. Quiet by default (outline); `solid` = solid ink; `signal` = the
 * screen's ONE primary action (signal fill — Submit job, Upload, Create +);
 * `danger` = destructive, outlined in the error colour, never filled.
 * (Design System §D.)
 */
export function GPill({ children, onClick, onCream = false, solid = false, signal = false, danger = false, disabled = false, style = {}, title }: {
  children: React.ReactNode; onClick?: () => void; onCream?: boolean; solid?: boolean;
  signal?: boolean; danger?: boolean;
  /** greyed out and inert, but still hoverable so `title` can say why (docs/design/capabilities.md) */
  disabled?: boolean;
  style?: React.CSSProperties; title?: string;
}) {
  const background = signal ? 'var(--accent)' : solid ? (onCream ? 'var(--on-cream)' : 'var(--fg)') : 'transparent';
  const color = signal ? 'var(--accent-ink)'
    : solid ? (onCream ? 'var(--cream)' : 'var(--bg-window)')
    : danger ? 'var(--s-err-fg)'
    : (onCream ? 'var(--on-cream)' : 'var(--fg-2)');
  const borderColor = signal || solid ? 'transparent' : danger ? 'var(--s-err-fg)' : (onCream ? 'var(--on-cream-3)' : 'var(--bd-strong)');
  return (
    <button onClick={disabled ? undefined : onClick} title={title} aria-disabled={disabled || undefined} style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      padding: '6px 14px',
      background, border: '1px solid', borderColor,
      borderRadius: 'var(--r-pill)',
      color,
      fontFamily: 'var(--ff-sans)', fontSize: 13, fontWeight: 500,
      cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
      opacity: disabled ? 0.45 : 1, ...style,
    }}>{children}</button>
  );
}

export function GMore({ onCream = false }: { onCream?: boolean }) {
  return <span style={{ color: onCream ? 'var(--on-cream-3)' : 'var(--fg-4)', fontSize: 18, letterSpacing: 1, cursor: 'pointer', userSelect: 'none', lineHeight: 1 }}>···</span>;
}

export function GCard({ cream = false, children, padding = 22, style = {}, onClick }: {
  cream?: boolean; children?: React.ReactNode; padding?: number | string;
  style?: React.CSSProperties; onClick?: () => void;
}) {
  return (
    // a card is a surface step, not an outlined box (push 4: borders are for rows)
    <div onClick={onClick} style={{
      background: cream ? 'var(--cream)' : 'var(--bg-panel)',
      borderRadius: 'var(--r-card)', padding,
      color: cream ? 'var(--on-cream)' : 'var(--fg)',
      ...style,
    }}>{children}</div>
  );
}

export function GTrend({ name, dir, onCream = false }: { name: string; dir: 'up' | 'down' | 'flat'; onCream?: boolean }) {
  const arrow = dir === 'up' ? '↑' : dir === 'down' ? '↓' : '→';
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontFamily: 'var(--ff-mono)', fontSize: 13.5, color: onCream ? 'var(--on-cream-2)' : 'var(--fg-2)', whiteSpace: 'nowrap' }}>
      <span>{name}</span>
      <span style={{ color: onCream ? 'var(--on-cream-3)' : 'var(--fg-3)' }}>{arrow}</span>
    </div>
  );
}

export type GStatusKind = 'ok' | 'warn' | 'err' | 'dim' | 'work';

/** mono status word with dot */
export function GStatus({ kind, label, onCream = false }: { kind: GStatusKind; label: string; onCream?: boolean }) {
  const c = {
    ok:   'var(--s-apv-fg)',
    warn: 'var(--s-review-fg)',
    err:  'var(--s-err-fg)',
    dim:  onCream ? 'var(--on-cream-3)' : 'var(--fg-4)',
    work: 'var(--s-work-fg)',
  }[kind];
  return (
    <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 12.5, color: c, whiteSpace: 'nowrap' }}>● <span style={{ marginLeft: 3 }}>{label}</span></span>
  );
}

export function GScreenHeader({ eyebrow, title, sub, right, back }: {
  eyebrow?: React.ReactNode; title: React.ReactNode; sub?: React.ReactNode; right?: React.ReactNode;
  back?: { label: string; onClick: () => void };
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '26px 28px 20px', gap: 24, flexShrink: 0 }}>
      <div style={{ minWidth: 0 }}>
        {back && (
          <button onClick={back.onClick} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: 'none', padding: 0, marginBottom: 4,
            fontFamily: 'var(--ff-mono)', fontSize: 12.5, color: 'var(--fg-3)', cursor: 'pointer',
          }}>← {back.label}</button>
        )}
        {eyebrow && <GEyebrow>{eyebrow}</GEyebrow>}
        <h1 style={{ margin: 0, fontFamily: 'var(--ff-sans)', fontSize: 32, fontWeight: 500, letterSpacing: '-0.03em', color: 'var(--fg)', whiteSpace: 'nowrap', lineHeight: 1.2 }}>{title}</h1>
        {sub && <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 13.5, color: 'var(--fg-3)', marginTop: 6, whiteSpace: 'nowrap' }}>{sub}</div>}
      </div>
      {right && <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

export function GToggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      // the toggle is the one pill-shaped control (Design System §C)
      width: 46, height: 26, borderRadius: 999, cursor: 'pointer', position: 'relative', flexShrink: 0,
      background: on ? 'var(--accent)' : 'var(--bg-elev)', border: '1px solid', borderColor: on ? 'var(--accent)' : 'var(--bd)',
      transition: 'background 0.15s',
    }}>
      <span style={{
        position: 'absolute', top: 2, left: on ? 22 : 2,
        width: 20, height: 20, borderRadius: '50%', background: on ? 'var(--accent-ink)' : 'var(--fg-3)',
        transition: 'left 0.15s',
      }} />
    </button>
  );
}

export const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 14px',
  background: 'var(--bg-base)', border: '1px solid var(--bd-soft)', borderRadius: 'var(--r-ctl)',
  outline: 'none', fontFamily: 'var(--ff-mono)', fontSize: 13.5, color: 'var(--fg)',
};

export function GField({ label, children, wide }: { label: React.ReactNode; children: React.ReactNode; wide?: boolean }) {
  return (
    <div style={{ gridColumn: wide ? '1 / -1' : 'auto' }}>
      <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 12, color: 'var(--fg-4)', marginBottom: 7 }}>{label}</div>
      {children}
    </div>
  );
}

export function GTabLink({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button onClick={onClick} style={{
      background: 'transparent', border: 'none', color: 'var(--accent)', cursor: 'pointer',
      fontFamily: 'var(--ff-mono)', fontSize: 12.5, padding: 0,
    }}>{children}</button>
  );
}

/**
 * Tabs — words slotted next to each other, the selected one underlined in
 * ink. Not buttons: no fills, no borders (design push 5, 2026-10-06; styles
 * in tokens.css under .g-tabs). Arrow keys move the selection; only the
 * selected tab is in the tab order, per the WAI tablist pattern.
 */
export function GTabs<T extends string>({ items, value, onChange, ariaLabel, style = {} }: {
  items: ReadonlyArray<{ id: T; label: React.ReactNode; title?: string }>;
  value: T;
  onChange: (id: T) => void;
  ariaLabel?: string;
  style?: React.CSSProperties;
}) {
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const i = items.findIndex(t => t.id === value);
    if (i < 0) return;
    const n = items.length;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (i + 1) % n;
    else if (e.key === 'ArrowLeft') next = (i - 1 + n) % n;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    if (next === null) return;
    e.preventDefault();
    onChange(items[next].id);
    (e.currentTarget.children[next] as HTMLElement | undefined)?.focus();
  };
  return (
    <div role="tablist" aria-label={ariaLabel} className="g-tabs" style={style} onKeyDown={onKeyDown}>
      {items.map(t => (
        <button key={t.id} type="button" role="tab" className="g-tab"
          aria-selected={t.id === value} tabIndex={t.id === value ? 0 : -1}
          title={t.title} onClick={() => onChange(t.id)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function GPrefSection({ label, children, last }: { label: string; children: React.ReactNode; last?: boolean }) {
  return (
    <div style={{ marginBottom: last ? 0 : 22, paddingBottom: last ? 0 : 22, borderBottom: last ? 'none' : '1px solid var(--bd-soft)' }}>
      <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 11.5, color: 'var(--fg-4)', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 14 }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>{children}</div>
    </div>
  );
}

export function GPrefRow({ title, sub, children }: { title: React.ReactNode; sub?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 14, color: 'var(--fg)' }}>{title}</div>
        {sub && <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 12, color: 'var(--fg-4)', marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}

/** Settings screen shell — settings render in the main window area, not modals. */
export function GSettingsShell({ title, eyebrow, onBack, children, footer, width = 720 }: {
  title: React.ReactNode; eyebrow?: React.ReactNode; onBack: () => void;
  children: React.ReactNode; footer?: React.ReactNode; width?: number;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <GScreenHeader back={{ label: 'Back', onClick: onBack }} eyebrow={eyebrow} title={title} />
      <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
        <GCard style={{ maxWidth: width }}>
          {children}
          {footer && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginTop: 26, paddingTop: 20, borderTop: '1px solid var(--bd-soft)' }}>{footer}</div>}
        </GCard>
      </div>
    </div>
  );
}

export function GGear({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

/**
 * Drag-to-resize hook for panels (sidebar, structure tree).
 * Returns a mousedown handler for the resize handle.
 */
export function useDragResize(width: number, setWidth: (w: number) => void) {
  return React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev: MouseEvent) => setWidth(startW + (ev.clientX - startX));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [width, setWidth]);
}

/** Vertical grip rendered inside a resizable panel's right edge. */
export function GResizeHandle({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  return (
    <div onMouseDown={onMouseDown} title="Drag to resize" className="tree-resize" style={{
      position: 'absolute', top: 0, right: -7, width: 14, height: '100%', cursor: 'col-resize', zIndex: 20,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div className="tree-resize-grip" style={{ width: 3, height: 40, borderRadius: 'var(--r-ctl)', background: 'var(--bd-strong)', opacity: 0.45, transition: 'opacity 0.12s, background 0.12s' }} />
    </div>
  );
}

/** Fixed full-screen click-away layer that sits under menus (menu itself goes z 30+). */
export function GClickAway({ onClick }: { onClick: () => void }) {
  return <div onClick={onClick} style={{ position: 'fixed', inset: 0, zIndex: 25 }} />;
}

/**
 * Danger confirmation dialog (same pattern as the sign-out confirm).
 * onConfirm may be async — the confirm button shows busyLabel while pending
 * and errors render inline instead of closing.
 */
export function GConfirmModal({ title, body, confirmLabel, busyLabel, onConfirm, onClose }: {
  title: string; body: React.ReactNode; confirmLabel: string; busyLabel?: string;
  onConfirm: () => Promise<void> | void; onClose: () => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={busy ? undefined : onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }} />
      <div style={{
        position: 'relative', width: 420,
        background: 'var(--bg-panel)', border: '1px solid var(--bd)', borderRadius: 'var(--r-card)', padding: 26,
      }}>
        <div style={{ fontFamily: 'var(--ff-sans)', fontSize: 18, fontWeight: 500, letterSpacing: '-0.02em', color: 'var(--fg)' }}>{title}</div>
        <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 13.5, color: 'var(--fg-3)', marginTop: 10, lineHeight: 1.55 }}>
          {body}
        </div>
        {error && (
          <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 12.5, color: 'var(--s-err-fg)', marginTop: 12, lineHeight: 1.5, wordBreak: 'break-word' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <GPill onClick={busy ? undefined : onClose}>Cancel</GPill>
          {/* destructive = outlined in the error colour, never filled (Design System §D) */}
          <button onClick={confirm} disabled={busy} style={{
            padding: '8px 18px', borderRadius: 'var(--r-pill)', cursor: busy ? 'default' : 'pointer',
            background: 'transparent', color: 'var(--s-err-fg)', border: '1px solid var(--s-err-fg)',
            fontFamily: 'var(--ff-sans)', fontSize: 13, fontWeight: 500,
            opacity: busy ? 0.7 : 1,
          }}>{busy ? (busyLabel || 'Working…') : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/** Small loading spinner for any component that's fetching its data. */
export function GSpinner({ size = 14, onCream = false }: { size?: number; onCream?: boolean }) {
  const stroke = onCream ? 'var(--on-cream-3)' : 'var(--fg-3)';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{ animation: 'g-spin 0.9s linear infinite', flexShrink: 0 }} aria-label="Loading">
      <circle cx="12" cy="12" r="9" stroke={stroke} strokeOpacity="0.25" strokeWidth="2.5" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

/** Spinner + label in the house loading style — the standard "fetching" row. */
export function GLoading({ label = 'Loading…', onCream = false, size = 14, style = {} }: {
  label?: string; onCream?: boolean; size?: number; style?: React.CSSProperties;
}) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 9,
      fontFamily: 'var(--ff-mono)', fontSize: 12.5,
      color: onCream ? 'var(--on-cream-3)' : 'var(--fg-3)',
      ...style,
    }}>
      <GSpinner size={size} onCream={onCream} />
      {label}
    </span>
  );
}

// ── async state: spinner while busy, error + Try again on failure ────────────
// Rule: every backend wait shows a spinner and every spinner has a timeout
// (services/ipc.ts) that lands in this error state — no infinite spinners.

export interface AsyncTaskState { busy: boolean; error: string | null }

/** run an async action with busy/error tracking; `retry` re-runs the last one */
export function useAsyncTask(): AsyncTaskState & {
  run: <T>(label: string, fn: () => Promise<T>) => Promise<T | undefined>;
  retry: () => Promise<void>;
  reset: () => void;
  label: string | null;
} {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [label, setLabel] = React.useState<string | null>(null);
  const last = React.useRef<{ label: string; fn: () => Promise<unknown> } | null>(null);
  // StrictMode mounts → unmounts → mounts once in dev: the flag must be
  // re-armed on every mount, or the "finished" updates are dropped and the
  // spinner never clears
  const alive = React.useRef(true);
  React.useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const run = React.useCallback(async <T,>(what: string, fn: () => Promise<T>): Promise<T | undefined> => {
    last.current = { label: what, fn };
    setBusy(true); setError(null); setLabel(what);
    try {
      const out = await fn();
      if (alive.current) setBusy(false);
      return out;
    } catch (err) {
      if (alive.current) {
        setBusy(false);
        const m = err instanceof Error ? err.message : String(err);
        setError(m.replace(/^Error invoking remote method '[^']+': Error: /, ''));
      }
      return undefined;
    }
  }, []);
  const retry = React.useCallback(async () => { if (last.current) await run(last.current.label, last.current.fn); }, [run]);
  const reset = React.useCallback(() => { setError(null); setBusy(false); }, []);
  return { busy, error, label, run, retry, reset };
}

/** the visual half: spinner + label while busy; message + Try again on error */
export function GAsyncState({ busy, error, label, onRetry, onCream = false, size = 12.5, style = {} }: {
  busy: boolean; error: string | null; label?: string | null;
  onRetry?: () => void; onCream?: boolean; size?: number; style?: React.CSSProperties;
}) {
  if (!busy && !error) return null;
  const fg = onCream ? 'var(--on-cream-2)' : 'var(--fg-3)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontFamily: 'var(--ff-mono)', fontSize: size, lineHeight: 1.45, ...style }}>
      {busy ? (
        <React.Fragment>
          <GSpinner size={size + 1} onCream={onCream} />
          <span style={{ color: fg }}>{label ?? 'Working…'}</span>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <span style={{ color: 'var(--s-err-fg)', overflowWrap: 'anywhere' }}>{error}</span>
          {onRetry && (
            <button onClick={onRetry} style={{
              padding: '4px 11px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
              border: `1px solid ${onCream ? 'var(--bd-strong)' : 'var(--bd)'}`,
              background: 'transparent', color: onCream ? 'var(--on-cream)' : 'var(--fg-2)',
              fontFamily: 'var(--ff-mono)', fontSize: size - 0.5,
            }}>Try again</button>
          )}
        </React.Fragment>
      )}
    </div>
  );
}

